<template>
  <div ref="rootElement" :class="$style.locationsFieldHolder" tabindex="1" @focusout="onFocusOut">
    <HgFormFieldWrapper
      :isDirty="$props.isDirty"
      :isInvalid="$props.isInvalid"
      :isRequired="$props.isRequired"
      :disabled="$props.disabled"
      :label="label"
    >
      <HgFormFieldStateIcons
        :isDirty="$props.isDirty"
        :isInvalid="$props.isInvalid"
        :isRequired="$props.isRequired"
        :disabled="$props.disabled"
      >
        <input
          :id="inputReferenceId"
          ref="searchField"
          autocomplete="off"
          :value="internalModel"
          :disabled="disabled"
          data-cy="Locations_searchField"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="souldDisplayDropdown ? 'true' : 'false'"
          :aria-controls="`${inputReferenceId}-list`"
          :style="transparentBackgroundStyle"
          @input="onInput"
          @focusin="onFocusIn"
        />
      </HgFormFieldStateIcons>
      <template v-for="(_, slotName) in $slots" :key="slotName" #[slotName]>
        <slot :name="slotName" />
      </template>
      <div
        :id="`${inputReferenceId}-list`"
        ref="locationSearchResults"
        :class="[$style.itemsHolder, { [$style.open]: souldDisplayDropdown }]"
        :style="itemsHolderStyles"
        data-cy="Locations_itemsHolder"
        role="listbox"
        :aria-hidden="souldDisplayDropdown ? 'false' : 'true'"
      >
        <p
          v-for="(item, index) in internalSearchResultItems"
          :key="`${inputReferenceId}-list-key-${index}`"
          :class="[$style.slicedItem, { [$style.activeItem]: isItemActive(index) }]"
          role="option"
          tabindex="-1"
          :aria-selected="isItemActive(index) ? 'true' : 'false'"
          :aria-posinset="index + 1"
          :aria-setsize="internalSearchResultItems.length"
          @click.prevent="onSelect(item)"
        >
          <span>
            <template
              v-for="(part, partIndex) in item.labelParts"
              :key="`${partIndex}-${part.text}-${part.isHighlighted}`"
            >
              <strong v-if="part.isHighlighted">{{ part.text }}</strong>
              <template v-else>{{ part.text }}</template>
            </template>
          </span>
        </p>
      </div>
    </HgFormFieldWrapper>
  </div>
</template>

<script setup lang="ts">
import { HgFormFieldStateIcons, HgFormFieldWrapper } from '@smg-real-estate/domus';
import throttle from 'lodash.throttle';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import getUniqueElementId from './getUniqueElementId';
import { searchGeoLocations } from './searchGeoLocations';
import setupElementStyles from './setupElementStyles';
import type { SelectableZipAndCity, ZipAndCity } from './types';

defineOptions({
  name: 'ZipCityLocations',
});

interface Props {
  selectedZip?: string;
  selectedCity?: string;
  label?: string;
  isDirty?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  transparentBackground?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectedZip: undefined,
  selectedCity: undefined,
  label: undefined,
  isDirty: false,
  isInvalid: false,
  isRequired: false,
  disabled: false,
  transparentBackground: undefined,
});

const emit = defineEmits<{
  locationChanged: [value: Partial<ZipAndCity>];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
}>();

const { locale } = useI18n();

const rootElement = ref<HTMLElement | null>(null);
const searchField = ref<HTMLInputElement | null>(null);
const locationSearchResults = ref<HTMLDivElement | null>(null);

const internalModel = ref('');
const internalSearchResultItems = ref<SelectableZipAndCity[]>([]);
const internalZip = ref(props.selectedZip ?? '');
const internalCity = ref(props.selectedCity ?? '');
const inputReferenceId = ref(getUniqueElementId('input'));
const selectionCursor = ref(-1);
const canDropdownOpen = ref(false);
const itemsHolderStyles = ref<Record<string, string | number> | null>(null);

interface ThrottledSearchLocations {
  (): void;
  cancel: () => void;
}

const souldDisplayDropdown = computed(
  () => canDropdownOpen.value && internalSearchResultItems.value.length > 0,
);
const selectedZipCity = computed(() =>
  props.selectedZip && props.selectedCity
    ? `${props.selectedZip} ${props.selectedCity}`.trim()
    : '',
);
const internalZipCity = computed(() => `${internalZip.value} ${internalCity.value}`.trim());
const transparentBackgroundStyle = computed(() =>
  props.transparentBackground ? { backgroundColor: 'transparent' } : undefined,
);

watch(selectedZipCity, (value) => {
  internalModel.value = value;
});

async function searchGeoLocationsInternal(): Promise<void> {
  if (internalModel.value.length < 2) {
    resetInternalResultItems();
    return;
  }

  internalSearchResultItems.value = await searchGeoLocations(internalModel.value, locale.value);
}
const searchLocations: ThrottledSearchLocations = throttle(
  searchGeoLocationsInternal,
  300,
) as ThrottledSearchLocations;

function setupDropdownElementStyles(): void {
  void nextTick(() => {
    if (!searchField.value) {
      return;
    }

    // remember that the max height argument (300) is also defined as a SASS variable in the style block.
    // in case of changes, both values must be adjusted.
    itemsHolderStyles.value = setupElementStyles(searchField.value, 300);
  });
}

function emitInternalValues(
  selectedZipCityItem: SelectableZipAndCity,
  updateInternalValue = true,
): void {
  internalZip.value = selectedZipCityItem.zip;
  internalCity.value = selectedZipCityItem.city;

  if (updateInternalValue) {
    internalModel.value = internalZipCity.value;
  }

  const emitArgs: Partial<ZipAndCity> = {
    canton: selectedZipCityItem.canton,
    city: selectedZipCityItem.city,
    zip: selectedZipCityItem.zip,
  };

  if (selectedZipCityItem.geoLocation) {
    Object.assign(emitArgs, {
      geoLocation: selectedZipCityItem.geoLocation,
    });
  }

  emit('locationChanged', emitArgs);
}

function resetInternalResultItems(): void {
  internalSearchResultItems.value = [];
}

function getSelectionByCursor(): SelectableZipAndCity | undefined {
  return internalSearchResultItems.value[selectionCursor.value];
}

function isItemActive(index: number): boolean {
  return index === selectionCursor.value;
}

function blurInputElement(): void {
  searchField.value?.blur();
}

function focusInputElement(): void {
  searchField.value?.focus();
}

function focusListElement(): void {
  const posInset = locationSearchResults.value?.querySelector(
    `[aria-posinset="${selectionCursor.value + 1}"]`,
  ) as HTMLElement | null;

  posInset?.focus({ preventScroll: false });
}

function onSelect(selectedItem?: SelectableZipAndCity): void {
  const selection = selectedItem || getSelectionByCursor();
  if (!selection) {
    return;
  }

  closedDropdown();
  blurInputElement();
  emitInternalValues(selection);
}

function onInput(event: Event): void {
  const inputElement = event.target as HTMLInputElement | null;
  if (!inputElement) {
    return;
  }

  selectionCursor.value = -1;
  internalModel.value = inputElement.value ?? '';
  searchLocations();
}

function onFocusIn(event: FocusEvent): void {
  emit('focus', event);
  canDropdownOpen.value = true;
  addListener();
}

function onFocusOut(event: FocusEvent): void {
  if (rootElement.value?.contains(event.relatedTarget as Node)) {
    return;
  }

  emit('blur', event);
  closedDropdown();

  if (internalModel.value && internalZipCity.value) {
    internalModel.value = internalZipCity.value;
    return;
  }

  if (internalModel.value && selectedZipCity.value) {
    internalModel.value = selectedZipCity.value;
    return;
  }

  if (!internalModel.value) {
    emitInternalValues({
      zip: '',
      city: '',
      canton: '',
    } as SelectableZipAndCity);
  }
}

function onKeyUp(): void {
  if (canDropdownOpen.value && selectionCursor.value > -1) {
    selectionCursor.value--;
  }

  if (selectionCursor.value < 0) {
    focusInputElement();
  } else {
    focusListElement();
  }
}

function onKeyDown(): void {
  if (canDropdownOpen.value && selectionCursor.value < internalSearchResultItems.value.length - 1) {
    selectionCursor.value++;
  }

  if (selectionCursor.value >= 0) {
    focusListElement();
  }
}

function closedDropdown(): void {
  canDropdownOpen.value = false;
  selectionCursor.value = -1;

  removeListener();
  resetInternalResultItems();
}

function dockKeyListener(event: KeyboardEvent): void {
  switch (event.code) {
    case 'ArrowUp':
      event.preventDefault();
      onKeyUp();
      return;
    case 'ArrowDown':
      event.preventDefault();
      onKeyDown();
      return;
    case 'Enter':
    case 'Space':
      event.preventDefault();
      onSelect();
      return;
    case 'Escape':
      closedDropdown();
      return;
  }
}

function addListener(): void {
  removeListener();
  document.addEventListener('keydown', dockKeyListener);
}

function removeListener(): void {
  document.removeEventListener('keydown', dockKeyListener);
}

onMounted(() => {
  internalModel.value = selectedZipCity.value;
  setupDropdownElementStyles();
});

onBeforeUnmount(() => {
  removeListener();
  searchLocations.cancel();
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

$dropdown-max-height: 300px;
$dropdown-max-height-large: 500px;

.locationsFieldHolder {
  position: relative;

  input {
    @include input;
  }

  [data-state='valid'] input {
    border-color: $color-green-60;
  }

  [data-state='error'] input {
    border-color: $color-orange-60;
  }

  .itemsHolder {
    position: absolute;
    z-index: 5;
    display: none;
    width: 100%;
    max-width: 100%;
    max-height: $dropdown-max-height-large;
    overflow-y: auto;
    background-color: $color-white;
    border-top: 1px solid $color-grey-50;
    border-bottom-right-radius: $border-radius;
    border-bottom-left-radius: $border-radius;
    box-shadow: $elevation-minimal;

    &.open {
      display: block;
    }

    // @media (min-width: $viewport-sm-min) {
    //   max-height: $dropdown-max-height;
    // }
  }

  .slicedItem {
    display: flex;
    justify-content: space-between;
    padding: $space-100;
    margin: 0;
    cursor: pointer;

    &:hover,
    &:focus,
    &.activeItem {
      background-color: $color-blue-0;
    }

    > span {
      @include truncate-text;

      & {
        color: $color-grey-90;
      }
    }
  }
}
</style>
